import { useState } from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { Recipe } from '../../types/mealPlan';
import { getRecipeFeedbackTags } from '../../utils/feedbackTags';
import { FeedbackTagChips } from './FeedbackTagChips';

type RecipeRatingModalProps = {
  opened: boolean;
  onClose: () => void;
  recipe: Recipe;
  userDietaryRestrictions?: string[];
  kids?: number; // household kid count; gates the kids chip
  onSubmit: (selectedTags: string[], dietaryViolations: string[]) => void;
};

export const RecipeRatingModal = ({
  opened,
  onClose,
  recipe,
  userDietaryRestrictions,
  kids = 0,
  onSubmit,
}: RecipeRatingModalProps) => {
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

  const allTags = getRecipeFeedbackTags(recipe, userDietaryRestrictions, {
    context: 'rating',
    kids,
  });

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    const selected = Array.from(selectedTags);
    // Extract dietary violations from selected tags and normalize to backend tag ID
    const dietaryViolations: string[] = [];
    const normalizedTags: string[] = [];
    for (const tagId of selected) {
      const tag = allTags.find((t) => t.id === tagId);
      if (tag?.dietaryViolation) {
        dietaryViolations.push(tag.dietaryViolation);
        normalizedTags.push('dietary_violation');
      } else {
        normalizedTags.push(tagId);
      }
    }
    // Deduplicate in case multiple dietary violation chips were selected
    const uniqueTags = Array.from(new Set(normalizedTags));
    onSubmit(uniqueTags, dietaryViolations);
    setSelectedTags(new Set());
  };

  const handleClose = () => {
    setSelectedTags(new Set());
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={600} size="lg">
          What didn't you like?
        </Text>
      }
      centered
      size="md"
      radius="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          This helps us improve your future meal plans.
        </Text>

        <FeedbackTagChips
          tags={allTags}
          selected={selectedTags}
          onToggle={toggleTag}
        />

        <Group justify="flex-end" mt="sm" gap="lg">
          <UnstyledButton onClick={handleClose}>
            <Text size="sm" c="dimmed">Cancel</Text>
          </UnstyledButton>
          <Button onClick={handleSubmit}>
            Submit
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
